import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button
} from '@material-ui/core';
import { 
  People,
  VideoLibrary,
  Visibility,
  Schedule,
  EmojiEvents,
  Favorite,
  TrendingUp,
  PlayArrow,
  Refresh,
  Assessment,
  Speed,
  Storage,
  ShowChart,
  Public
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import adminService from '../../services/adminService';
import { toast } from 'react-toastify';
import { getRankLabel, getRankStyle } from '../../utils/rankStyles';
import {
  fetchAdminDashboardData,
  normalizeAdminDashboardData,
} from '../../utils/adminDashboardData';
import './AdminDashboard.css';

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: 'transparent',
    minHeight: '60vh',
    padding: '0'
  },
  glassCard: {
    borderRadius: 'var(--radius-card)',
    background: 'var(--bg-white)',
    border: '1px solid var(--color-ploom-alpha-10)',
    boxShadow: 'var(--shadow-card)',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-card-hover)'
    }
  },
  header: {
    marginBottom: '36px',
    textAlign: 'left'
  },
  title: {
    color: 'var(--color-text-black)',
    fontFamily: 'var(--font-title)',
    fontWeight: 400,
    marginBottom: '10px'
  },
  subtitle: {
    color: 'var(--color-text-body)',
    fontFamily: 'var(--font-body)',
    fontSize: '16px',
    lineHeight: 1.7
  },
  statCard: {
    borderRadius: 'var(--radius-card)',
    background: 'var(--bg-white)',
    border: '1px solid var(--color-ploom-alpha-10)',
    boxShadow: 'var(--shadow-card)',
    height: '100%',
    minHeight: '198px',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: 'var(--shadow-card-hover)'
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'var(--color-ploom)',
      borderRadius: 'var(--radius-card) var(--radius-card) 0 0'
    }
  },
  statIcon: {
    fontSize: '2.5rem',
    color: 'var(--color-ploom)',
    marginBottom: '14px'
  },
  statValue: {
    color: 'var(--color-text-black)',
    fontFamily: 'var(--font-title)',
    fontSize: '2.5rem',
    fontWeight: 400,
    lineHeight: 1,
    marginBottom: '10px'
  },
  statLabel: {
    color: 'var(--color-text-body)',
    fontFamily: 'var(--font-body)',
    fontSize: '16px',
    fontWeight: 400
  },
  filterSection: {
    marginBottom: '34px',
    display: 'flex',
    gap: '18px',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '24px',
    borderRadius: 'var(--radius-card)',
    background: 'var(--bg-white)',
    border: '1px solid var(--color-ploom-alpha-10)',
    boxShadow: 'var(--shadow-card)'
  },
  modernTabs: {
    '& .MuiTabs-root': {
      background: 'var(--bg-light-ploom)',
      borderRadius: 'var(--radius-small)',
      padding: '8px'
    },
    '& .MuiTab-root': {
      color: 'var(--color-ploom)',
      fontFamily: 'var(--font-body)',
      borderRadius: '12px',
      margin: '0 4px',
      minHeight: '48px',
      transition: 'background 0.25s ease, color 0.25s ease',
      '&.Mui-selected': {
        background: 'var(--bg-white)',
        color: 'var(--color-ploom)',
        fontWeight: 600,
        boxShadow: 'var(--shadow-btn)'
      }
    },
    '& .MuiTabs-indicator': {
      display: 'none'
    }
  },
  chartCard: {
    borderRadius: 'var(--radius-card)',
    background: 'var(--bg-white)',
    border: '1px solid var(--color-ploom-alpha-10)',
    boxShadow: 'var(--shadow-card)',
    padding: 'clamp(24px, 3vw, 36px)'
  },
  tableCard: {
    borderRadius: 'var(--radius-card)',
    background: 'var(--bg-white)',
    border: '1px solid var(--color-ploom-alpha-10)',
    boxShadow: 'var(--shadow-card)',
    overflow: 'hidden'
  },
  tableHeader: {
    background: 'var(--color-ploom)',
    '& th': {
      color: 'var(--bg-white)',
      fontWeight: 600,
      borderBottom: 'none',
      padding: '20px 16px'
    }
  },
  tableRow: {
    background: 'var(--bg-white)',
    '&:nth-child(even)': {
      background: 'var(--bg-light-ploom)'
    },
    '&:hover': {
      background: 'var(--color-ploom-alpha-10)'
    }
  },
  rankChip: {
    fontWeight: 600,
    textTransform: 'capitalize',
    borderRadius: 'var(--radius-pill)',
    border: '0'
  },
  modernButton: {
    borderRadius: 'var(--radius-pill)',
    background: 'var(--color-ploom)',
    color: 'var(--bg-white)',
    fontFamily: 'var(--font-body)',
    fontWeight: 400,
    textTransform: 'none',
    padding: '12px 24px',
    boxShadow: 'var(--shadow-btn)',
    border: '1.5px solid var(--color-ploom)',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    '&:hover': {
      background: 'var(--bg-white)',
      color: 'var(--color-ploom)',
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-btn-hover)'
    }
  },
  modernSelect: {
    borderRadius: '12px',
    background: 'var(--bg-white)',
    '& .MuiSelect-select': {
      padding: '12px 16px'
    }
  },
  progressCard: {
    borderRadius: 'var(--radius-card)',
    background: 'var(--bg-white)',
    border: '1px solid var(--color-ploom-alpha-10)',
    boxShadow: 'var(--shadow-card)',
    padding: '28px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  progressText: {
    color: 'var(--color-text-body)',
    fontSize: '16px'
  }
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className="admin-tab-panel-content">
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminDashboard = ({ compact = false }) => {
  const classes = useStyles();
  const user = useSelector(state => state.user);
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('30d');
  const [refreshing, setRefreshing] = useState(false);
  const dashboardData = normalizeAdminDashboardData(analytics);
  const rankChart = dashboardData.users.byRank.slice(0, 6);
  const countryChart = dashboardData.users.byCountry.slice(0, 6);
  const activityChart = dashboardData.activityTimeline.slice(-7);
  const maxRankCount = Math.max(1, ...rankChart.map((item) => item.count));
  const maxCountryCount = Math.max(1, ...countryChart.map((item) => item.count));
  const maxActivityCount = Math.max(1, ...activityChart.map((item) => Number(item.activity) || 0));
  const activePercentage = dashboardData.users.total > 0
    ? Math.round((dashboardData.users.active / dashboardData.users.total) * 100)
    : 0;

  const loadAnalytics = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await fetchAdminDashboardData(
        adminService,
        timeRange,
        user?.user?.email
      );

      setAnalytics(result.analytics);
      toast.success('Datele dashboardului au fost actualizate');
      if (result.source === 'legacy') {
        toast.info('Sunt afișate datele analytics disponibile');
      }
    } catch (error) {
      console.error('Error loading admin analytics:', error);
      setError(error.message || 'Datele analytics nu au putut fi încărcate');
      toast.error('Datele dashboardului nu au putut fi încărcate');
    } finally {
      setLoading(false);
    }
  }, [timeRange, user?.user?.email]);

  const refreshAnalytics = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
  };

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  if (loading) {
    return (
      <Box className={classes.progressCard}>
        <CircularProgress size={40} style={{ color: 'var(--color-ploom)' }} />
        <Box>
          <Typography variant="h6" style={{ color: 'var(--color-ploom)', marginBottom: '4px' }}>
            Se încarcă dashboardul...
          </Typography>
          <Typography className={classes.progressText}>
            Pregătim indicatorii și graficele
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Card className={classes.glassCard} style={{ padding: '24px', textAlign: 'center' }}>
        <Typography variant="h6" style={{ color: 'var(--color-error)', marginBottom: '16px' }}>
          Dashboardul nu a putut fi încărcat
        </Typography>
        <Typography style={{ color: 'var(--color-text-body)', marginBottom: '16px' }}>
          {error}
        </Typography>
        <Button 
          onClick={loadAnalytics} 
          className={classes.modernButton}
          startIcon={<Refresh />}
        >
          Încearcă din nou
        </Button>
      </Card>
    );
  }

  return (
    <Box className={`${classes.root} admin-dashboard-modern`}>
      {/* Header */}
      {!compact && (
        <Box className={classes.header}>
          <Typography variant={compact ? "h5" : "h4"} className={classes.title}>
            Tablou de bord
          </Typography>
          <Typography className={classes.subtitle}>
            Indicatori, activitate și performanță într-o singură privire
          </Typography>
        </Box>
      )}

      {/* Enhanced Filter Section */}
      <Box className={classes.filterSection}>
        <FormControl variant="outlined" size="small" className={classes.modernSelect}>
          <InputLabel>Perioadă</InputLabel>
          <Select
            value={timeRange}
            onChange={handleTimeRangeChange}
            label="Perioadă"
          >
            <MenuItem value="7d">Ultimele 7 zile</MenuItem>
            <MenuItem value="30d">Ultimele 30 de zile</MenuItem>
            <MenuItem value="90d">Ultimele 90 de zile</MenuItem>
            <MenuItem value="1y">Ultimul an</MenuItem>
          </Select>
        </FormControl>
        <Button 
          className={classes.modernButton}
          onClick={refreshAnalytics}
          disabled={refreshing}
          startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
          size="small"
        >
          {refreshing ? 'Se actualizează...' : 'Actualizează'}
        </Button>
        <Chip 
          icon={<TrendingUp />}
          label="Date actuale"
          style={{ 
            background: 'var(--bg-light-ploom)',
            color: 'var(--color-ploom)',
            border: '1px solid var(--color-ploom-alpha-18)'
          }}
        />
      </Box>

      {/* Enhanced Overview Stats */}
      {analytics && (
        <Grid container spacing={4} className="admin-kpi-grid">
          {[
            { icon: People, value: dashboardData.users.total, label: 'Utilizatori' },
            { icon: TrendingUp, value: dashboardData.users.active, label: 'Utilizatori activi' },
            { icon: VideoLibrary, value: dashboardData.videos.total, label: 'Videoclipuri' },
            { icon: Visibility, value: dashboardData.videos.totalViews, label: 'Vizualizări' },
            { icon: Schedule, value: `${dashboardData.videos.totalWatchTime}m`, label: 'Timp vizionare' },
            { icon: EmojiEvents, value: dashboardData.engagement.totalCertificates, label: 'Finalizări' },
            { icon: Favorite, value: dashboardData.engagement.newFavorites, label: 'Favorite' },
            { icon: PlayArrow, value: `${dashboardData.performance.completionRate}%`, label: 'Rată finalizare' }
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className={classes.statCard}>
                <CardContent style={{ textAlign: 'center' }}>
                  <stat.icon className={classes.statIcon} />
                  <Typography className={classes.statValue}>
                    {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <section className="admin-visual-grid" aria-label="Grafice de performanță">
        <article className="admin-chart-card admin-chart-card--wide">
          <div className="admin-chart-heading">
            <div>
              <span>STRUCTURĂ ECHIPĂ</span>
              <h3>Distribuție pe rank</h3>
            </div>
            <People aria-hidden="true" />
          </div>
          <div className="admin-horizontal-chart">
            {rankChart.length ? rankChart.map((item) => (
              <div className="admin-bar-row" key={item.rank}>
                <span className="admin-bar-label">{getRankLabel(item.rank)}</span>
                <div className="admin-bar-track" aria-hidden="true">
                  <span
                    style={{
                      width: `${Math.max(6, Math.round((item.count / maxRankCount) * 100))}%`,
                      background: getRankStyle(item.rank).backgroundColor,
                    }}
                  />
                </div>
                <strong>{item.count}</strong>
              </div>
            )) : <p className="admin-chart-empty">Distribuția va apărea imediat ce există date.</p>}
          </div>
        </article>

        <article className="admin-chart-card">
          <div className="admin-chart-heading">
            <div>
              <span>ACOPERIRE</span>
              <h3>Top regiuni</h3>
            </div>
            <Public aria-hidden="true" />
          </div>
          <div className="admin-column-chart" role="img" aria-label="Distribuția utilizatorilor pe regiuni">
            {countryChart.length ? countryChart.map((item) => (
              <div className="admin-column-item" key={item.country}>
                <strong>{item.count}</strong>
                <span
                  className="admin-column-bar"
                  style={{ height: `${Math.max(14, Math.round((item.count / maxCountryCount) * 100))}%` }}
                  aria-hidden="true"
                />
                <small title={item.country}>{item.country}</small>
              </div>
            )) : <p className="admin-chart-empty">Nu există încă date geografice.</p>}
          </div>
        </article>

        <article className="admin-chart-card">
          <div className="admin-chart-heading">
            <div>
              <span>RITM</span>
              <h3>Activitate recentă</h3>
            </div>
            <ShowChart aria-hidden="true" />
          </div>
          <div className="admin-column-chart admin-column-chart--activity" role="img" aria-label="Activitate în ultimele intervale">
            {activityChart.length ? activityChart.map((item) => (
              <div className="admin-column-item" key={item.date}>
                <strong>{Number(item.activity) || 0}</strong>
                <span
                  className="admin-column-bar"
                  style={{ height: `${Math.max(10, Math.round(((Number(item.activity) || 0) / maxActivityCount) * 100))}%` }}
                  aria-hidden="true"
                />
                <small>{String(item.date || '').slice(5)}</small>
              </div>
            )) : <p className="admin-chart-empty">Activitatea zilnică va fi afișată aici.</p>}
          </div>
        </article>

        <article className="admin-chart-card admin-chart-card--gauges">
          <div className="admin-chart-heading">
            <div>
              <span>SĂNĂTATE CONT</span>
              <h3>Rate cheie</h3>
            </div>
            <Speed aria-hidden="true" />
          </div>
          <div className="admin-gauge-grid">
            {[
              { label: 'Activitate', value: activePercentage },
              { label: 'Finalizare', value: dashboardData.performance.completionRate },
              { label: 'Interacțiune', value: dashboardData.performance.engagementRate },
            ].map((gauge) => {
              const value = Math.max(0, Math.min(100, Number(gauge.value) || 0));
              return (
                <div className="admin-gauge-item" key={gauge.label}>
                  <div
                    className="admin-gauge"
                    role="img"
                    aria-label={`${gauge.label}: ${value}%`}
                    style={{ background: `conic-gradient(var(--color-ploom) ${value * 3.6}deg, var(--color-ploom-alpha-10) 0deg)` }}
                  >
                    <span>{value}%</span>
                  </div>
                  <small>{gauge.label}</small>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      {/* Enhanced Analytics Tabs */}
      <Card className={classes.chartCard}>
        <Box className={classes.modernTabs}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            textColor="inherit"
            indicatorColor="primary"
          >
            <Tab label="Distribuție utilizatori" icon={<Assessment />} />
            <Tab label="Conținut popular" icon={<TrendingUp />} />
            <Tab label="Activitate recentă" icon={<Schedule />} />
            <Tab label="Performanță" icon={<ShowChart />} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={4}>
            {/* Users by Rank */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" className="admin-table-section-title">
                Utilizatori după rank
              </Typography>
              <Card className={classes.tableCard}>
                <Table>
                  <TableHead className={classes.tableHeader}>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell align="right">Număr</TableCell>
                      <TableCell align="right">%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.users.byRank.map((rankData, index) => (
                        <TableRow key={index} className={classes.tableRow}>
                          <TableCell>
                            <Chip 
                              label={getRankLabel(rankData.rank)}
                              className={classes.rankChip}
                              style={getRankStyle(rankData.rank)}
                            />
                          </TableCell>
                          <TableCell align="right" style={{ fontWeight: 'bold' }}>{rankData.count}</TableCell>
                          <TableCell align="right" style={{ color: 'var(--color-ploom)' }}>{rankData.percentage}%</TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </Card>
            </Grid>

            {/* Users by Country */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" className="admin-table-section-title">
                Utilizatori după regiune
              </Typography>
              <Card className={classes.tableCard}>
                <Table>
                  <TableHead className={classes.tableHeader}>
                    <TableRow>
                    <TableCell>Regiune</TableCell>
                    <TableCell align="right">Număr</TableCell>
                      <TableCell align="right">%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.users.byCountry
                        .slice(0, 10)
                        .map((countryData, index) => (
                          <TableRow key={index} className={classes.tableRow}>
                            <TableCell style={{ fontWeight: '500' }}>{countryData.country}</TableCell>
                            <TableCell align="right" style={{ fontWeight: 'bold' }}>{countryData.count}</TableCell>
                            <TableCell align="right" style={{ color: 'var(--color-ploom)' }}>{countryData.percentage}%</TableCell>
                          </TableRow>
                        ))
                    }
                  </TableBody>
                </Table>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" className="admin-table-section-title">
            Cele mai urmărite videoclipuri
          </Typography>
          <Card className={classes.tableCard}>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                <TableCell>Videoclip</TableCell>
                <TableCell align="right">Vizualizări</TableCell>
                <TableCell align="right">Poziție</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.videos.popularVideos.map((video, index) => (
                  <TableRow key={video.videoId} className={classes.tableRow}>
                    <TableCell style={{ fontWeight: '500', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {video.title || `Video ${video.videoId}`}
                    </TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold', color: 'var(--color-ploom)' }}>{video.views.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`#${video.rank || index + 1}`}
                        size="small"
                        style={{ 
                          background: index < 3 ? 'var(--color-ploom)' : 'var(--bg-light-ploom)',
                          color: index < 3 ? 'var(--bg-white)' : 'var(--color-ploom)'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" className="admin-table-section-title">
            Activitate recentă
          </Typography>
          <Card className={classes.tableCard}>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                <TableCell>ID utilizator</TableCell>
                <TableCell>Activitate</TableCell>
                <TableCell>Dată</TableCell>
                <TableCell>Detalii</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.recentActivities.slice(0, 20).map((activity, index) => (
                  <TableRow key={`${activity.userId}-${index}`} className={classes.tableRow}>
                    <TableCell style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{activity.userId?.substring(0, 8)}...</TableCell>
                    <TableCell>
                      <Chip 
                        label={activity.activity?.replace('_', ' ') || 'Activity'}
                        size="small"
                        style={{ 
                          backgroundColor: 'var(--color-ploom)',
                          color: 'var(--bg-white)'
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ fontSize: '0.9rem' }}>
                      {new Date(activity.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell style={{ fontSize: '0.8rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {activity.details || 'No details'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card className={classes.glassCard} style={{ padding: '20px', textAlign: 'center' }}>
                <Speed style={{ fontSize: '3rem', color: 'var(--color-ploom)', marginBottom: '16px' }} />
                <Typography variant="h4" style={{ color: 'var(--color-text-black)', fontFamily: 'var(--font-title)' }}>
                  {dashboardData.overview.avgSessionTime}
                </Typography>
                <Typography variant="body2" style={{ color: 'var(--color-text-body)' }}>
                  Durata medie a sesiunii (minute)
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className={classes.glassCard} style={{ padding: '20px', textAlign: 'center' }}>
                <Storage style={{ fontSize: '3rem', color: 'var(--color-ploom)', marginBottom: '16px' }} />
                <Typography variant="h4" style={{ color: 'var(--color-text-black)', fontFamily: 'var(--font-title)' }}>
                  {dashboardData.overview.storageUsed}
                </Typography>
                <Typography variant="body2" style={{ color: 'var(--color-text-body)' }}>
                  Spațiu utilizat (GB)
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default AdminDashboard;
